import { useScienceTutorSection, tutorSelection } from './scienceTutorContext';
import { CheckCircle2, XCircle } from 'lucide-react';
import { useState } from 'react';

import { checkPrediction, type ModelQuestion } from './atomicModels';

export function PredictionQuestion({
  question,
  checkLabel = 'Check prediction',
  onChecked,
}: {
  question: ModelQuestion;
  checkLabel?: string;
  onChecked?: (correct: boolean) => void;
}) {
  const [selected, setSelected] = useState('');
  const [checked, setChecked] = useState(false);
  useScienceTutorSection('PredictionQuestion', { selected: tutorSelection(selected), checked });
  const result = checked && selected ? checkPrediction(question, selected) : null;

  return (
    <div className="rounded-[1.5rem] border border-hairline bg-white p-4 sm:p-5">
      <p className="text-sm font-extrabold leading-6 text-ink">{question.prompt}</p>
      <div className="mt-3 grid gap-2" role="group" aria-label={question.prompt}>
        {question.options.map((option) => (
          <button
            key={option.id}
            type="button"
            aria-pressed={selected === option.id}
            onClick={() => {
              setSelected(option.id);
              setChecked(false);
            }}
            className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold transition-colors motion-reduce:transition-none ${
              selected === option.id
                ? 'border-brand-coral bg-wash-coral text-ink'
                : 'border-hairline bg-white text-ink-soft hover:border-brand-coral/40'
            }`}
          >
            {option.label}
          </button>
        ))}
      </div>
      <button
        type="button"
        disabled={!selected}
        onClick={() => {
          setChecked(true);
          const outcome = checkPrediction(question, selected);
          if (outcome) onChecked?.(outcome.correct);
        }}
        className="mt-3 rounded-full bg-ink px-5 py-2.5 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
      >
        {checkLabel}
      </button>
      {result ? (
        <div
          role="region"
          aria-label={`Feedback: ${question.id}`}
          className={`mt-3 rounded-2xl border p-4 ${
            result.correct ? 'border-brand-mint/45 bg-wash-mint' : 'border-brand-coral/45 bg-wash-coral'
          }`}
        >
          <p className="flex items-center gap-2 text-sm font-extrabold text-ink">
            {result.correct ? (
              <CheckCircle2 className="h-4 w-4 text-brand-mint" aria-hidden="true" />
            ) : (
              <XCircle className="h-4 w-4 text-brand-coral" aria-hidden="true" />
            )}
            {result.correct ? 'Correct' : 'Not quite'}
          </p>
          <p className="mt-1 text-sm font-medium leading-6 text-ink-soft">{result.feedback}</p>
        </div>
      ) : null}
    </div>
  );
}
